/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { later, cancel } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import {
  autosaveTinyMCEConfig,
  autosaveCreateEditorId,
  manageAutosaveDraftsButton,
} from '@linc-technologies/linced-common/utils/tinymce';
import { action, computed } from '@ember/object';
import ThresholdLevel from 'linced-app/enums/threshold-level';
import { processTemplate } from '@linc-technologies/linced-common/utils/post-templates';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import moment from 'moment';

export default class AttendanceLetter extends Component {
  // Services
  @service
  dates;

  @service
  expulsions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  letterContent = '';
  threshold = null;
  responseActivities = [];
  @tracked
  mode = 'preview';
  @tracked
  nominatedSchoolPerson = null;
  previousMeetingDate = null;
  futureMeetingDate = null;
  followUpDate = null;
  includeFollowUpDate = true;
  includeFutureMeetingDate = true;
  includePreviousMeetingDate = true;
  caregivers = [];
  ThresholdLevel = ThresholdLevel;

  fieldPlaceholder(personType, fieldKey, label) {
    return `<span class="hero-template-placeholder" data-placeholder-type="field" data-placeholder-person-type="${personType}" data-placeholder-for="${fieldKey}">${label}</span>`;
  }

  basicPlaceholder(key, label) {
    return `<span class="hero-template-placeholder" data-placeholder-type="basic" data-placeholder-for="${key}">${label}</span>`;
  }

  attendancePlaceholder(key, label) {
    return `<span class="hero-template-placeholder" data-placeholder-type="attendance" data-placeholder-for="${key}">${label}</span>`;
  }

  // editorId returns an element ID to assign to the editor's children
  @computed('session.person.id')
  get editorId() {
    let userId = this.session.person.get('id');
    return autosaveCreateEditorId(userId, null, null);
  }

  @computed('editorId')
  get editorOptions() {
    return Object.freeze(autosaveTinyMCEConfig(this.editorId));
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    // TinyMCE autosave plugin
    // Manage the button's state on initial insertion
    manageAutosaveDraftsButton(this.editorId);
  }

  willUpdate() {
    super.willUpdate(...arguments);
    // TinyMCE autosave plugin
    // Manage the button's state on initial update
    manageAutosaveDraftsButton(this.editorId);
  }

  didUpdate() {
    super.didUpdate(...arguments);

    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
    this.timer = later(
      this,
      function () {
        manageAutosaveDraftsButton(this.editorId);
      },
      3000,
    );
  }

  // Date validations
  followUpDateValidations = Object.freeze([
    {
      message: 'Follow-Up Date must be in the future',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrAfter(now, 'd');
      },
    },
  ]);

  futureMeetingDateValidations = Object.freeze([
    {
      message: 'Future Meeting Date must be in the future',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrAfter(now, 'd');
      },
    },
  ]);

  previousMeetingDateValidations = Object.freeze([
    {
      message: 'Previous Meeting Date must be in the past',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrBefore(now, 'd');
      },
    },
  ]);

  @tracked
  timer;

  @tracked
  dialogOpen;

  getAttendanceContext(learner, absences, responseActivities) {
    return {
      absences,
      learnerFirstName: learner.firstName,
      author: this.get('session.person'),
      nominatedSchoolPerson: this.nominatedSchoolPerson,
      responseActivities,
      previousMeetingDate: this.previousMeetingDate,
      followUpDate: this.followUpDate,
      futureMeetingDate: this.futureMeetingDate,
    };
  }

  getAttendanceTemplate(thresholdValue) {
    const learnerFirstName = this.fieldPlaceholder(
      1,
      'core:firstNamePreferred',
      'First name (preferred)',
    );
    const learnerLastName = this.fieldPlaceholder(
      1,
      'core:lastNameLegal',
      'Last name (legal)',
    );
    const learnerFullName = `${learnerFirstName} ${learnerLastName}`;
    const schoolName = this.basicPlaceholder('school_name', 'School name');
    const staffName = this.attendancePlaceholder(
      'nominated_school_person_name',
      '{{Staff_member_name}}',
    );
    const staffEmail = this.attendancePlaceholder(
      'nominated_school_person_email',
      '{{Staff_member_email}}',
    );
    const authorName = this.attendancePlaceholder('author_name', 'Author name');
    // const authorEmail = attendancePlaceholder(
    //   'author_email',
    //   'Author email address'
    // );
    const attendanceDays = this.attendancePlaceholder(
      'attendance_days_absent',
      'Number of days absent',
    );
    const responseActivities = this.attendancePlaceholder(
      'response_activities',
      'Response Activities',
    );

    const previousMeetingDate = this.attendancePlaceholder(
      'previous_meeting_date',
      '{{Previous_meeting_date}}',
    );
    const followUpDate = this.attendancePlaceholder(
      'follow_up_date',
      '{{Follow_up_date}}',
    );
    const future_meeting_date = this.attendancePlaceholder(
      'future_meeting_date',
      '{{Future_meeting_date}}',
    );

    if (isEmpty(thresholdValue)) {
      return '';
    }

    let futureMeetingDateNote = this.includeFutureMeetingDate
      ? `<p>Please let me know if ${future_meeting_date} works or send me a message to arrange a time.</p>`
      : '';
    let previousMeetingNote = this.includePreviousMeetingDate
      ? `, and the attendance rate hasn't improved as much as we'd hoped since our last meeting on ${previousMeetingDate}`
      : '';
    let followUpNote = this.includeFollowUpDate
      ? `<p>If there are insufficient improvements by ${followUpDate}, the board will consider the appropriate next steps available, which may include a referral to the Ministry of Education to consider prosecution for irregular attendance under section 244 of the Education and Training Act 2020.</p>`
      : '';

    switch (thresholdValue) {
      case ThresholdLevel.GREEN:
        return `
          <h3>Our expectations for student attendance</h3>
          <p>Tēnā koe,</p>
          <p>Regular attendance at ${schoolName} is important to your child's success and there is a clear connection between going to school regularly and doing well in the classroom. Making sure students attend and engage in learning is a shared responsibility. As a parent or caregiver, you are responsible for making sure your child attends school every day.</p>
          <p>If your child is going to be absent for any reason it is essential that you let us know as soon as possible. Please phone us, send us an email, or message us through Hero to confirm their absence and provide the reason.</p>
          <p>We acknowledge that there are genuine reasons students may occasionally be absent. However, without regular attendance your child will struggle to make progress. If your child is absent regularly or for extended periods, we will reach out to you to discuss the situation.</p>
          <p>We are committed to supporting your child to attend school. If you're having difficulty getting your child to school, you can ask for support by:</p>
          <ul>
            <li>Speaking with your child's teacher,</li>
            <li>Getting in contact with ${staffName} on ${staffEmail}</li>
          </ul>
          <p>We can work with you to develop a support plan that addresses any barriers to regular attendance.</p>
          <p>More information on attendance, including your rights and responsibilities as parents or caregivers, is available on the Ministry of Education's <a href="https://www.education.govt.nz/parents-and-caregivers/schools-year-0-13/attendance" target="_blank" rel="noreferrer noopener">attendance webpage</a>.</p>
          <p>Ngā mihi,</p>
          <p>${authorName}</p>
        `;
      case ThresholdLevel.YELLOW:
        return `
          <p><strong>Discussion about ${learnerFirstName}'s attendance</strong></p>
          <p>Tēnā koe,</p>
          <p>I'm reaching out to talk about ${learnerFirstName}'s attendance this term. ${learnerFirstName}'s been absent for ${attendanceDays} days this term.</p>
          <p>We know that regular attendance plays a big part in helping students feel connected and succeed in their learning. I'd really value the opportunity to talk with you about how things are going and see how we can work together to support ${learnerFirstName} to attend more regularly.</p>
          ${futureMeetingDateNote}
          <p>Please contact ${staffName} on ${staffEmail} as soon as possible with any questions you may have.</p>
          <p>More information on attendance, including your rights and responsibilities as parents or caregivers, is available on the Ministry of Education's <a href="https://www.education.govt.nz/parents-and-caregivers/schools-year-0-13/attendance" target="_blank" rel="noreferrer noopener">attendance webpage</a>.</p>
          <p>Ngā mihi,</p>
          <p>${authorName}</p>
        `;
      case ThresholdLevel.ORANGE:
        return `
          <p><strong>${learnerFullName} – continued low attendance</strong></p>
          <p>Tēnā koe,</p>
          <p>I'm getting in touch to talk about ${learnerFirstName}'s attendance. We've noticed ${learnerFirstName} has been absent for ${attendanceDays} days this term${previousMeetingNote}.</p>
          <p>We know that regular attendance helps students feel connected, confident, and successful in their learning. We also understand that there can be challenges, and we're here to work with you to find solutions that support ${learnerFirstName} to be at school more consistently.</p>
          <p>Let's meet to create a plan that helps ${learnerFirstName} get back on track with their learning.</p>
          <p>Please contact ${staffName} on ${staffEmail} as soon as possible with any questions you may have.</p>
          <p>More information on attendance, including your rights and responsibilities as parents or caregivers, is available on the Ministry of Education's <a href="https://www.education.govt.nz/parents-and-caregivers/schools-year-0-13/attendance" target="_blank" rel="noreferrer noopener">attendance webpage</a>.</p>
          <p>Ngā mihi,</p>
          <p>${authorName}</p>
        `;
      case ThresholdLevel.RED:
        return `
          <p><strong>Referral / consideration of legal action</strong></p>
          <p>Tēnā koe,</p>
          <p>This is to inform you that ${learnerFullName} has not had regular attendance at ${schoolName}.</p>
          <p>This letter is to remind you of your legal obligation to ensure that ${learnerFirstName} attends school whenever it is open and to request that ${learnerFirstName} returns to regular school attendance immediately.</p>
          ${responseActivities}
          <p>Section 36 of the Education and Training Act requires a student to attend the registered school at which they are enrolled whenever it is open, while section 244 provides that a parent of that student commits an offence if they do not attend as required by section 36.</p>
          ${followUpNote}
          <p>Please contact ${staffName} on ${staffEmail} as soon as possible with any questions you may have.</p>
          <p>More information on attendance, including your rights and responsibilities as parents or caregivers, is available on the Ministry of Education's <a href="https://www.education.govt.nz/parents-and-caregivers/schools-year-0-13/attendance" target="_blank" rel="noreferrer noopener">attendance webpage</a>.</p>
          <p>Yours sincerely / Nāku iti nei, nā,</p>
          <p>${authorName}</p>
        `;
    }

    return '';
  }

  @action
  setDateFieldToggle(field, value) {
    this.set(field, value);
    this.letterContent = '';
    this.generateLetter();
  }

  @action
  setDateField(field, value) {
    this.set(field, value);
    this.letterContent = '';
    this.generateLetter();
  }

  @action
  removeNominatedPerson() {
    this.nominatedSchoolPerson = null;
    this.letterContent = '';
  }

  @action
  setNominatedPerson(person) {
    this.nominatedSchoolPerson = person;
    this.letterContent = '';
    this.generateLetter();
  }

  @action
  generateLetter() {
    if (isEmpty(this.letterContent)) {
      this.letterContent = processTemplate(
        this.getAttendanceTemplate(this.threshold),
        {
          school: this.get('session.school'),
          person: this.learner,
          attendance: this.getAttendanceContext(
            this.learner,
            this.absences,
            this.responseActivities,
          ),
        },
      );
    }
  }

  @action
  updateLetterContent(value) {
    this.letterContent = value;
  }

  @action
  setMode(mode) {
    this.mode = mode;
  }

  @action
  closeDialog() {
    this.letterContent = '';
    this.mode = 'preview';
    this.dialogOpen = false;
  }

  @action
  createAttendancePost() {
    if (isEmpty(this.attendancePage)) {
      this.toaster.error(
        'No attendance page found. Please create an attendance page first.',
      );
      this.closeDialog();
      return;
    }
    let postParams = {
      postType: PostType.POST,
      status: PostStatus.DRAFT,
      schoolId: this.get('session.school'),
      authorId: this.get('session.person'),
      personId: this.learner,
      pageId: this.attendancePage,
      content: this.letterContent,
      subject: `Attendance Notice`,
    };
    let post = this.store.createRecord('posts/post', postParams);
    post
      .save()
      .then(() => {
        this.toaster.success('Attendance Post added successfully.');
        this.closeDialog();
      })
      .catch(() => {
        this.toaster.error('Error creating Attendance Post');
      });
  }
}
